import { useState } from 'react';
import { Card, SLabel, WBtn } from '../components/Atoms';
import { C, VF, calcNEWS2, vitalColor } from '../constants';
import { toDay, fmtDay, byDate } from '../utils';

function TrendArrow({ val, prev, field }) {
  if (!val || !prev || val === prev) return null;
  const n = parseFloat(val), p = parseFloat(prev);
  if (isNaN(n) || isNaN(p) || n === p) return null;
  const up = n > p;
  const isGoodUp = field === 'vO2';
  const color = up ? (isGoodUp ? '#30D158' : '#FF4545') : (isGoodUp ? '#FF4545' : '#30D158');
  return <span style={{ fontSize: 9, color, marginLeft: 3 }}>{up ? '\u2191' : '\u2193'}</span>;
}

function FluidBalance({ ioLog }) {
  if (!ioLog || ioLog.length < 1) return null;
  const daysWithData = ioLog.filter(d => (d.inputs || []).length > 0 || (d.outputs || []).length > 0);
  if (!daysWithData.length) return null;
  // Cumulative totals across all recorded days since admission
  const totalIn  = daysWithData.reduce((s, d) => s + (d.inputs  || []).reduce((a, e) => a + (parseFloat(e.amount) || 0), 0), 0);
  const totalOut = daysWithData.reduce((s, d) => s + (d.outputs || []).reduce((a, e) => a + (parseFloat(e.amount) || 0), 0), 0);
  const net = totalIn - totalOut;
  if (totalIn === 0 && totalOut === 0) return null;
  const netCol = net > 1000 ? '#FF9500' : net < -1000 ? '#64D2FF' : '#30D158';
  const netBg  = net > 1000 ? '#1A0E00' : net < -1000 ? '#071420' : '#071A0C';
  const netLabel = net > 1000 ? 'Fluid overload' : net < -1000 ? 'Fluid deficit' : 'Balanced';
  const nDays = daysWithData.length;
  return (
    <div style={{ background: netBg, border: '1px solid ' + netCol + '44', borderRadius: 10, padding: '10px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: netCol + 'AA', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>Cumulative Net Balance ({nDays} day{nDays !== 1 ? 's' : ''})</div>
        <div style={{ fontSize: 28, fontWeight: 900, color: netCol, fontFamily: 'monospace', lineHeight: 1 }}>{net >= 0 ? '+' : ''}{net.toFixed(0)} <span style={{ fontSize: 14, fontWeight: 600 }}>mL</span></div>
        <div style={{ fontSize: 10, color: netCol + '88', marginTop: 3 }}>{netLabel}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0, textAlign: 'right' }}>
        <div style={{ fontSize: 11, color: '#30D158' }}>IN <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{totalIn.toFixed(0)}</span></div>
        <div style={{ fontSize: 11, color: '#4A9EFF' }}>OUT <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{totalOut.toFixed(0)}</span></div>
      </div>
    </div>
  );
}

const IORow = ({ e, type, date, editIOEntry, delIOEntry }) => (
  <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
    <input value={e.label} onChange={ev => editIOEntry(date, type, e.id, 'label', ev.target.value)} placeholder={type === 'inputs' ? 'e.g. IVF, PO' : 'e.g. Urine, NG'} style={{ flex: 1, minWidth: 0, background: C.bg3, border: '1px solid ' + C.bd, borderRadius: 5, padding: '4px 6px', color: C.tx, fontSize: 11 }} />
    <input value={e.amount} onChange={ev => editIOEntry(date, type, e.id, 'amount', ev.target.value)} placeholder="mL" style={{ width: 48, flexShrink: 0, background: C.bg3, border: '1px solid ' + C.bd, borderRadius: 5, padding: '4px 4px', color: type === 'inputs' ? '#30D158' : '#4A9EFF', fontSize: 11, fontFamily: "'IBM Plex Mono',monospace", textAlign: 'right' }} />
    <button onClick={() => delIOEntry(date, type, e.id)} style={{ background: 'none', border: 'none', color: C.dim2, cursor: 'pointer', fontSize: 13, padding: 0, flexShrink: 0 }}>x</button>
  </div>
);

const GCS_E = [
  [4, 'Spontaneous'],
  [3, 'To sound'],
  [2, 'To pain'],
  [1, 'None'],
];
const GCS_V = [
  [5, 'Oriented'],
  [4, 'Confused'],
  [3, 'Inapprop. words'],
  [2, 'Sounds'],
  [1, 'None'],
];
const GCS_M = [
  [6, 'Obeys commands'],
  [5, 'Localizes pain'],
  [4, 'Withdraws'],
  [3, 'Decorticate'],
  [2, 'Decerebrate'],
  [1, 'None'],
];



function ScoreBadge({ score, label, color, bg }) {
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', background: color + '22', border: '1.5px solid ' + color + '66', borderRadius: 8, padding: '4px 10px', flexShrink: 0, minWidth: 44 }}>
      <span style={{ fontSize: 18, fontWeight: 900, color, lineHeight: 1 }}>{score}</span>
      <span style={{ fontSize: 8, fontWeight: 800, color: color + 'BB', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
    </div>
  );
}

export function calcGCS(v) {
  if (!v || !v.vGCSe || !v.vGCSv || !v.vGCSm) return null;
  const e = parseInt(v.vGCSe), vb = parseInt(v.vGCSv), m = parseInt(v.vGCSm);
  if (isNaN(e) || isNaN(vb) || isNaN(m)) return null;
  const total = e + vb + m;
  const color = total >= 13 ? '#30D158' : total >= 9 ? '#FFD60A' : '#FF4545';
  const bg    = total >= 13 ? '#071A0C'  : total >= 9 ? '#1A1500' : '#1A0505';
  const risk  = total >= 13 ? 'Mild' : total >= 9 ? 'Moderate' : 'Severe';
  return { total, e, v: vb, m, color, bg, risk };
}

const inputLike = { width: '100%', background: C.bg3, border: '1.5px solid ' + C.bd, borderRadius: 6, padding: '6px 8px', color: C.tx, fontSize: 13, colorScheme: 'dark', cursor: 'pointer', fontFamily: "'IBM Plex Mono',monospace", appearance: 'auto' };

function GCSPicker({ vd, setVd }) {
  const gcs = calcGCS(vd);
  const col = gcs ? gcs.color : C.dim;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.dim }}>
          GCS
          {gcs && <span style={{ marginLeft: 6, color: col, fontWeight: 900 }}>{gcs.total} <span style={{ fontSize: 8, opacity: 0.8 }}>E{gcs.e}V{gcs.v}M{gcs.m}</span></span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        {[
          { field: 'vGCSe', ph: 'Eye',    opts: GCS_E },
          { field: 'vGCSv', ph: 'Verbal', opts: GCS_V },
          { field: 'vGCSm', ph: 'Motor',  opts: GCS_M },
        ].map(({ field, ph, opts }) => (
          <select key={field} value={vd[field] || ''} onChange={ev => setVd(x => ({ ...x, [field]: ev.target.value }))}
            style={{ ...inputLike, flex: 1, minWidth: 0, color: vd[field] ? col : C.dim2 }}>
            <option value='' style={{ color: C.dim2 }}>{ph}</option>
            {opts.map(([s, d]) => <option key={s} value={s} style={{ color: C.tx }}>{s} {d}</option>)}
          </select>
        ))}
      </div>
    </div>
  );
}


const LEDGER_FIELDS = [
  { k: 'vBP',  lb: 'BP',   unit: 'mmHg', color: '#FF6B6B' },
  { k: 'vHR',  lb: 'HR',   unit: 'bpm',  color: '#FF9500' },
  { k: 'vRR',  lb: 'RR',   unit: '/min', color: '#FFD60A' },
  { k: 'vT',   lb: 'Temp', unit: '°C',   color: '#30D158' },
  { k: 'vO2',  lb: 'SpO2', unit: '%',    color: '#4A9EFF' },
];

function parseNum(v) { return v ? parseFloat(v) : NaN; }
function parseSys(bp) { return bp ? parseFloat((bp||'').split('/')[0]) : NaN; }

function VitalsLedger({ vlog }) {
  const now = Date.now();
  const latest = vlog[0];

  // Cutoff as naive local wall-clock string — matches how entries are stored (no timezone)
  const _c = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const cutoffStr = _c.getFullYear() + '-' +
    String(_c.getMonth()+1).padStart(2,'0') + '-' +
    String(_c.getDate()).padStart(2,'0') + 'T' +
    String(_c.getHours()).padStart(2,'0') + ':' +
    String(_c.getMinutes()).padStart(2,'0');
  const recent = vlog.filter(v => ((v.date || '') + 'T' + (v.time || '00:00')) >= cutoffStr);

  const getRange = (k) => {
    if (k === 'vBP') {
      const sys = recent.map(v => parseFloat((v[k]||'').split('/')[0])).filter(n => !isNaN(n));
      const dia = recent.map(v => parseFloat((v[k]||'').split('/')[1])).filter(n => !isNaN(n));
      if (!sys.length) return null;
      const sRange = Math.min(...sys) === Math.max(...sys) ? `${Math.min(...sys)}` : `${Math.min(...sys)}–${Math.max(...sys)}`;
      const dRange = dia.length ? (Math.min(...dia) === Math.max(...dia) ? `${Math.min(...dia)}` : `${Math.min(...dia)}–${Math.max(...dia)}`) : null;
      return dRange ? `${sRange} / ${dRange}` : sRange;
    }
    const vals = recent.map(v => parseNum(v[k])).filter(n => !isNaN(n));
    if (!vals.length) return null;
    const mn = Math.min(...vals), mx = Math.max(...vals);
    return mn === mx ? `${mn}` : `${mn}–${mx}`;
  };

  return (
    <div style={{ background: C.bg2, border: '1px solid ' + C.bd, borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
      {/* header */}
      <div style={{ display: 'grid', gridTemplateColumns: '56px repeat(5, 1fr)', borderBottom: '1px solid ' + C.bd }}>
        <div style={{ padding: '7px 10px', fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.08em' }} />
        {LEDGER_FIELDS.map(f => (
          <div key={f.k} style={{ padding: '7px 4px', fontSize: 9, fontWeight: 800, color: f.color, textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'center' }}>
            {f.lb}
          </div>
        ))}
      </div>

      {/* latest row */}
      <div style={{ display: 'grid', gridTemplateColumns: '56px repeat(5, 1fr)', borderBottom: '1px solid ' + C.bd + '66' }}>
        <div style={{ padding: '9px 10px', fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center' }}>Latest</div>
        {LEDGER_FIELDS.map(f => {
          const raw = latest?.[f.k];
          const display = raw || '—';
          const col = raw ? f.color : C.dim2;
          return (
            <div key={f.k} style={{ padding: '9px 4px', textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: col, fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1.1 }}>{display}</div>
              {raw && <div style={{ fontSize: 8, color: C.dim2, marginTop: 1 }}>{f.unit}</div>}
            </div>
          );
        })}
      </div>

      {/* 24h range row */}
      <div style={{ display: 'grid', gridTemplateColumns: '56px repeat(5, 1fr)' }}>
        <div style={{ padding: '8px 10px', fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', lineHeight: 1.3 }}>24h<br/>Range</div>
        {LEDGER_FIELDS.map(f => {
          const range = getRange(f.k);
          return (
            <div key={f.k} style={{ padding: '8px 4px', textAlign: 'center' }}>
              <div style={{ fontSize: f.k === 'vBP' ? 9 : 11, fontWeight: 700, color: range ? f.color + 'BB' : C.dim2, fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1.1 }}>{range || '—'}</div>
              {range && <div style={{ fontSize: 8, color: C.dim2, marginTop: 1 }}>{f.unit}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function VitalsTab({ cur, vd, setVd, saveVitals, delVitals, getIODay, addIOEntry, editIOEntry, delIOEntry, delIODay }) {
  const vlog     = [...(cur.vitalsLog || [])].sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
  const today    = toDay();
  const ioLog    = [...(cur.ioLog || [])].sort((a, b) => b.date.localeCompare(a.date));
  const allDates = [...new Set([today, ...ioLog.map(d => d.date)])];
  const latestV  = vlog[0] || null;
  const [showForm, setShowForm] = useState(false);
  const news2    = calcNEWS2(latestV);
  const gcs      = calcGCS(latestV);

  return (
    <>
      {gcs && (
        <div style={{ background: gcs.bg, border: '1px solid ' + gcs.color + '44', borderRadius: 10, padding: '10px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
          <ScoreBadge score={gcs.total} label="GCS" color={gcs.color} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: gcs.color }}>{gcs.risk} — GCS {gcs.total}</div>
            <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>E{gcs.e}V{gcs.v}M{gcs.m} · Eye {gcs.e} · Verbal {gcs.v} · Motor {gcs.m}</div>
          </div>
        </div>
      )}
      {news2 && (
        <div style={{ background: news2.bg, border: '1px solid ' + news2.color + '44', borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <ScoreBadge score={news2.score} label="NEWS2" color={news2.color} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: news2.color }}>NEWS2 Score {news2.score} — {news2.risk} Risk</div>
            <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>{news2.reasons.length ? news2.reasons.join(' · ') : 'All parameters within normal range'}</div>
          </div>
        </div>
      )}

      {vlog.length > 0 && <VitalsLedger vlog={vlog} />}

      {!showForm && (
        <button onClick={() => setShowForm(true)} style={{
          width: '100%', padding: '11px 0', borderRadius: 10, border: '1px dashed #2A4A6A',
          background: 'transparent', color: '#4A9EFF', fontSize: 13, fontWeight: 700,
          cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
          ＋ New Vitals Entry
        </button>
      )}

      {showForm && (
      <Card s={{ border: '1px solid #2A4A6A' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <SLabel color="#4A9EFF" mb={0}>New Entry</SLabel>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <input type="date" value={vd.date} onChange={e => setVd(v => ({ ...v, date: e.target.value }))} style={{ background: C.bg3, border: '1px solid ' + C.bd, borderRadius: 6, padding: '4px 6px', color: C.tx, fontSize: 11, colorScheme: 'dark' }} />
            <input type="time" value={vd.time} onChange={e => setVd(v => ({ ...v, time: e.target.value }))} style={{ background: C.bg3, border: '1px solid ' + C.bd, borderRadius: 6, padding: '4px 6px', color: C.tx, fontSize: 11, colorScheme: 'dark' }} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: 10 }}>
          {VF.filter(f => f.k !== 'vAVPU' && f.k !== 'vGCS').map(f => (
            <div key={f.k}>
              <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.dim, marginBottom: 3 }}>{f.lb} <span style={{ opacity: 0.6 }}>{f.unit}</span></div>
              <input value={vd[f.k]} onChange={e => setVd(v => ({ ...v, [f.k]: e.target.value }))} placeholder={f.ph} style={{ width: '100%', background: C.bg3, border: '1.5px solid ' + C.bd, borderRadius: 6, padding: '6px 8px', color: C.tx, fontSize: 13, fontFamily: "'IBM Plex Mono',monospace" }} />
            </div>
          ))}
          {/* GCS — occupies the right cell of the SpO2 row */}
          <GCSPicker vd={vd} setVd={setVd} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.dim, marginBottom: 5 }}>AVPU (Consciousness)</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['A','Alert','#30D158'],['V','Voice','#FFD60A'],['P','Pain','#FF9500'],['U','Unresponsive','#FF4545']].map(([opt, lbl, col]) => {
              const active = (vd.vAVPU || 'A') === opt;
              return (
                <button key={opt} onClick={() => setVd(v => ({ ...v, vAVPU: opt }))} style={{ flex: 1, padding: '6px 4px', borderRadius: 7, border: '1px solid ' + (active ? col : C.bd), background: active ? col + '22' : C.bg3, color: active ? col : C.dim, fontSize: 10, fontWeight: active ? 800 : 400, cursor: 'pointer' }}>
                  <div style={{ fontWeight: 800 }}>{opt}</div>
                  <div style={{ fontSize: 8, opacity: 0.8 }}>{lbl}</div>
                </button>
              );
            })}
          </div>
        </div>
        <WBtn onClick={() => { saveVitals(); setShowForm(false); }}>+ Save Vitals</WBtn>
        <button onClick={() => setShowForm(false)}
          style={{ width: '100%', marginTop: 6, padding: '7px 0', borderRadius: 10, background: 'transparent', border: 'none', color: C.dim2, fontSize: 12, cursor: 'pointer' }}>
          ✕ Cancel
        </button>
      </Card>
      )}

      {vlog.length === 0 && <div style={{ textAlign: 'center', color: C.dim2, fontSize: 13, padding: '20px 0' }}>No vitals recorded</div>}
      {vlog.length > 0 && Object.entries(byDate(vlog)).map(([date, entries]) => {
        const cols = entries.slice().reverse();
        return (
          <div key={date} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#4A9EFF', letterSpacing: '0.08em', marginBottom: 6 }}>{fmtDay(date)}</div>
            <div style={{ background: C.bg2, borderRadius: 12, border: '1px solid ' + C.bd, overflowX: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '80px repeat(' + cols.length + ',1fr)', minWidth: 200 }}>
                <div style={{ padding: '7px 10px', fontSize: 9, color: C.dim2, fontWeight: 800, borderBottom: '1px solid ' + C.bd }}>Vital</div>
                {cols.map((v, ci) => {
                  const isLatest = ci === cols.length - 1;
                  const n2 = isLatest ? calcNEWS2(v) : null;
                  return (
                    <div key={v.id} style={{ padding: '7px 8px', background: isLatest ? '#0A1E2E' : undefined, borderBottom: '1px solid ' + C.bd, borderLeft: '1px solid ' + C.bd, position: 'relative' }}>
                      <button onClick={() => delVitals(v.id)} style={{ position: 'absolute', top: 4, right: 4, background: 'none', border: 'none', color: C.dim2, cursor: 'pointer', fontSize: 10, padding: 0, lineHeight: 1 }} title="Delete">×</button>
                      <div style={{ fontSize: 10, color: isLatest ? '#4A9EFF' : C.dim, fontWeight: 600 }}>{v.time}</div>
                      {isLatest && <div style={{ fontSize: 8, color: '#4A9EFF', fontWeight: 700 }}>LATEST</div>}
                      {n2 && <div style={{ marginTop: 3 }}><ScoreBadge score={n2.score} label="NEWS2" color={n2.color} /></div>}
                    </div>
                  );
                })}
              </div>
              {VF.map((f, fi) => (
                <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '80px repeat(' + cols.length + ',1fr)', borderTop: fi > 0 ? '1px solid ' + C.bd : undefined }}>
                  <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase' }}>{f.lb}</div>
                    <div style={{ fontSize: 8, color: C.dim2 }}>{f.unit}</div>
                  </div>
                  {cols.map((v, ci) => {
                    const fk = f.k;
                    let val, displayVal;
                    if (fk === 'vGCS') {
                      const e2 = v.vGCSe ? parseInt(v.vGCSe) : null;
                      const v2 = v.vGCSv ? parseInt(v.vGCSv) : null;
                      const m2 = v.vGCSm ? parseInt(v.vGCSm) : null;
                      val = (e2 && v2 && m2) ? String(e2 + v2 + m2) : null;
                      displayVal = val ? `${e2 + v2 + m2} E${e2}V${v2}M${m2}` : '—';
                    } else {
                      val = v[fk];
                      displayVal = fk === 'vAVPU' ? (val || 'A') : (val || '—');
                    }
                    const prev = ci > 0 ? cols[ci - 1][fk] : null;
                    const isLatest = ci === cols.length - 1;
                    const vc = isLatest && fk !== 'vAVPU' && fk !== 'vGCS' ? vitalColor(fk, val) : null;
                    const gcsCol = (fk === 'vGCS' && val) ? calcGCS({ vGCSe: v.vGCSe, vGCSv: v.vGCSv, vGCSm: v.vGCSm })?.color : null;
                    return (
                      <div key={v.id} style={{ padding: '8px 8px', borderLeft: '1px solid ' + C.bd, background: vc ? vc.bg : (isLatest ? '#0A1E2E' : undefined), display: 'flex', alignItems: 'center', borderRadius: vc ? 4 : 0 }}>
                        <span style={{ fontSize: fk === 'vAVPU' ? 11 : (fk === 'vGCS' ? 10 : 13), fontFamily: (fk !== 'vAVPU' && fk !== 'vGCS') ? "'IBM Plex Mono',monospace" : undefined, color: vc ? vc.color : (gcsCol || (isLatest ? '#E0EEFA' : C.dim)), fontWeight: isLatest ? 700 : 400 }}>{displayVal}</span>
                        {fk !== 'vAVPU' && fk !== 'vGCS' && val && prev && <TrendArrow val={val} prev={prev} field={fk} />}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <div style={{ display: 'flex', alignItems: 'center', margin: '8px 0' }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: '#64D2FF', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Fluid Balance</div>
      </div>

      <FluidBalance ioLog={ioLog.filter(d => d.inputs?.length > 0 || d.outputs?.length > 0)} />

      {allDates.map(date => {
        const day = getIODay(date);
        const totalIn  = day.inputs.reduce((s, e)  => s + (parseFloat(e.amount) || 0), 0);
        const totalOut = day.outputs.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
        const net = totalIn - totalOut;
        const netColor = net > 500 ? '#FF9500' : net < -500 ? '#64D2FF' : '#30D158';
        return (
          <Card key={date} s={{ border: '1px solid #1A3A4A22', marginBottom: 6, padding: '8px 10px' }}>
            {/* Date row */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#64D2FF' }}>{date === today ? 'Today \u2014 ' : ''}{fmtDay(date)}</div>
              {(totalIn > 0 || totalOut > 0) && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 5, alignItems: 'center' }}>
                  <span style={{ fontSize: 9, color: '#30D158' }}>IN {totalIn.toFixed(0)}</span>
                  <span style={{ fontSize: 9, color: '#4A9EFF' }}>OUT {totalOut.toFixed(0)}</span>
                  <span style={{ fontSize: 10, fontWeight: 800, color: netColor }}>NET {net >= 0 ? '+' : ''}{net.toFixed(0)}</span>
                </div>
              )}
              {day.id && <button onClick={() => delIODay(date)} style={{ background: 'none', border: 'none', color: C.dim2, cursor: 'pointer', fontSize: 11, marginLeft: 6, padding: 0 }}>×</button>}
            </div>
            {/* Inputs */}
            <div style={{ marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
                <span style={{ fontSize: 8, fontWeight: 800, color: '#30D158', letterSpacing: '0.08em', textTransform: 'uppercase' }}>In</span>
                <button onClick={() => addIOEntry(date, 'inputs')} style={{ marginLeft: 'auto', background: 'none', border: 'none', borderRadius: 4, color: '#30D158', cursor: 'pointer', fontSize: 10, padding: 0 }}>+ Add</button>
              </div>
              {day.inputs.length === 0 && <div style={{ fontSize: 10, color: C.dim2 }}>—</div>}
              {day.inputs.map(e => <IORow key={e.id} e={e} type="inputs" date={date} editIOEntry={editIOEntry} delIOEntry={delIOEntry} />)}
              {totalIn > 0 && <div style={{ fontSize: 10, fontWeight: 700, color: '#30D158', textAlign: 'right' }}>= {totalIn.toFixed(0)} mL</div>}
            </div>
            {/* Outputs */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
                <span style={{ fontSize: 8, fontWeight: 800, color: '#4A9EFF', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Out</span>
                <button onClick={() => addIOEntry(date, 'outputs')} style={{ marginLeft: 'auto', background: 'none', border: 'none', borderRadius: 4, color: '#4A9EFF', cursor: 'pointer', fontSize: 10, padding: 0 }}>+ Add</button>
              </div>
              {day.outputs.length === 0 && <div style={{ fontSize: 10, color: C.dim2 }}>—</div>}
              {day.outputs.map(e => <IORow key={e.id} e={e} type="outputs" date={date} editIOEntry={editIOEntry} delIOEntry={delIOEntry} />)}
              {totalOut > 0 && <div style={{ fontSize: 10, fontWeight: 700, color: '#4A9EFF', textAlign: 'right' }}>= {totalOut.toFixed(0)} mL</div>}
            </div>
          </Card>
        );
      })}
      <button onClick={() => { const d = window.prompt('Add I&O for date (YYYY-MM-DD):', toDay()); if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) addIOEntry(d, 'inputs'); }} style={{ width: '100%', padding: 9, borderRadius: 10, background: C.bg2, border: '1px dashed ' + C.bd, color: C.dim, fontSize: 12, cursor: 'pointer', marginBottom: 8 }}>
        + Add Past Date
      </button>
    </>
  );
}
